<script lang="ts">
    import RewardsActionButton from "@apps/rewards/components/Buttons/RewardsActionButton.svelte";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/Theme/RewardsGeneralTheme.svelte";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { map } from "rxjs";

    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";

    //Props
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let locationController: LocationControllerInterface;
    export let urlsBuilder: URLsBuilder;
    export let namespace: RewardsTranslationNamespaces;

    //Variables
    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));
    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();

    //Handlers
    const handleRedirect = (link: string): void => {
        locationController.goTo(link);
    };
</script>

{#if $rewardsBranding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$rewardsBranding$.general}>
        <div class="gw-rd-empty-state-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    <RewardsCustomerStatusBasedContent
                        isCustomerContent={$isCustomer$}
                    >
                        <svelte:fragment slot="customer-content">
                            <RewardsPageWidgetTitle>
                                <T
                                    {namespace}
                                    translationKey="customer_title"
                                />
                            </RewardsPageWidgetTitle>
                        </svelte:fragment>
                        <svelte:fragment slot="default-content">
                            <RewardsPageWidgetTitle>
                                <T {namespace} translationKey="guest_title" />
                            </RewardsPageWidgetTitle>
                            <div
                                slot="default-content"
                                class="gw-rd-empty-state-page-widget__btns"
                            >
                                <RewardsActionButton
                                    variant="primary"
                                    on:click={() =>
                                        handleRedirect(
                                            urlsBuilder.createRegisterUrl()
                                        )}
                                >
                                    <Text
                                        component="span"
                                        variant="base"
                                        weight="semibold"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="sign_up_btn_label"
                                        />
                                    </Text>
                                </RewardsActionButton>
                                <RewardsActionButton
                                    variant="secondary"
                                    on:click={() =>
                                        handleRedirect(
                                            urlsBuilder.createLoginUrl()
                                        )}
                                >
                                    <Text
                                        component="span"
                                        variant="base"
                                        weight="semibold"
                                    >
                                        <T
                                            {namespace}
                                            translationKey="log_in_btn_label"
                                        />
                                    </Text>
                                </RewardsActionButton>
                            </div>
                        </svelte:fragment>
                    </RewardsCustomerStatusBasedContent>
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss">
    .gw-rd-empty-state-page-widget {
        background-color: var(--gw-white-bg);

        &__btns {
            display: flex;
            gap: var(--gw-rd-space-10);
            justify-content: center;

            :global(.gw-btn.gw-rd-action-btn) {
                padding: clamp(
                        var(--gw-rd-space-4),
                        3.7px + 1.3vw,
                        var(--gw-rd-space-7)
                    )
                    var(--gw-rd-space-15);
                font-size: clamp(14px, 12.5px + 0.44vw, 16px);
            }

            :global(.gw-btn.gw-rd-action-btn--primary) {
                min-width: 158px;
            }
        }
    }
</style>
